import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Plus, PencilSimple, Trash, GlobeSimple, Play, Database, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY_CONFIG = { name: "", base_url: "", search_paths: "", selectors_title: "", selectors_entity: "", schedule_interval: 60, is_active: true };

export default function ScraperConfigPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CONFIG);
  const [saving, setSaving] = useState(false);
  const [ocdsYear, setOcdsYear] = useState("2026");
  const [ocdsMax, setOcdsMax] = useState("200");
  const [ocdsImporting, setOcdsImporting] = useState(false);

  const fetchConfigs = async () => {
    try {
      const res = await api.get("/scraper/configs");
      setConfigs(res.data.configs);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchConfigs(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_CONFIG);
    setDialogOpen(true);
  };

  const openEdit = (cfg) => {
    setEditing(cfg.id);
    setForm({
      name: cfg.name,
      base_url: cfg.base_url,
      search_paths: (cfg.search_paths || []).join(", "),
      selectors_title: cfg.selectors?.title || "",
      selectors_entity: cfg.selectors?.entity || "",
      schedule_interval: cfg.schedule_interval || 60,
      is_active: cfg.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      name: form.name,
      base_url: form.base_url,
      search_paths: form.search_paths.split(",").map(s => s.trim()).filter(Boolean),
      selectors: { title: form.selectors_title, entity: form.selectors_entity },
      schedule_interval: parseInt(form.schedule_interval) || 60,
      is_active: form.is_active,
    };
    try {
      if (editing) {
        await api.put(`/scraper/configs/${editing}`, payload);
        toast.success("Configuración actualizada");
      } else {
        await api.post("/scraper/configs", payload);
        toast.success("Configuración creada");
      }
      setDialogOpen(false);
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta configuración?")) return;
    try {
      await api.delete(`/scraper/configs/${id}`);
      toast.success("Configuración eliminada");
      fetchConfigs();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleStartJob = async (configId) => {
    try {
      await api.post(`/scraper/jobs?config_id=${configId}`);
      toast.success("Trabajo de scraping iniciado. Revise el Monitor para ver el progreso.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar scraping");
    }
  };

  const handleOcdsImport = async () => {
    setOcdsImporting(true);
    try {
      await api.post("/scraper/import-ocds", { year: ocdsYear, max_records: parseInt(ocdsMax) || 200 });
      toast.success(`Importación OCDS ${ocdsYear} iniciada. Revise el Monitor para ver el progreso.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar importación");
    } finally { setOcdsImporting(false); }
  };

  return (
    <div data-testid="scraper-config-page" className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tight">Configuración de Scrapers</h1>
          <p className="text-muted-foreground text-sm mt-1">Administra las fuentes de datos y reglas de scraping</p>
        </div>
        <Button data-testid="add-config-button" className="rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white" onClick={openNew}>
          <Plus size={16} className="mr-2" /> Nueva Fuente
        </Button>
      </div>

      {/* OCDS Import Panel */}
      <Card className="rounded-none shadow-none border-2 border-[#2A9D8F]/30 bg-[#2A9D8F]/[0.03]" data-testid="ocds-import-panel">
        <CardContent className="p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <Database size={24} weight="duotone" className="text-[#2A9D8F] flex-shrink-0 mt-1" />
            <div className="flex-1 min-w-[280px]">
              <p className="text-sm font-medium">Importar desde OCDS (Open Contracting Data Standard)</p>
              <p className="text-xs text-muted-foreground mt-1">
                Descarga datos reales de licitaciones de GuateCompras desde el registro abierto de contrataciones. No requiere CAPTCHA.
              </p>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label className="text-xs font-mono text-muted-foreground">Año:</Label>
                  <Select value={ocdsYear} onValueChange={setOcdsYear}>
                    <SelectTrigger className="rounded-none h-9 w-24 bg-card text-sm" data-testid="ocds-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="2026">2026</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2024">2024</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs font-mono text-muted-foreground">Registros:</Label>
                  <Select value={ocdsMax} onValueChange={setOcdsMax}>
                    <SelectTrigger className="rounded-none h-9 w-24 bg-card text-sm" data-testid="ocds-max-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                      <SelectItem value="200">200</SelectItem>
                      <SelectItem value="500">500</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="ocds-import-button"
                  className="rounded-none bg-[#2A9D8F] hover:bg-[#228578] text-white px-6"
                  onClick={handleOcdsImport}
                  disabled={ocdsImporting}
                >
                  {ocdsImporting ? <><Spinner size={16} className="mr-2 animate-spin" /> Importando...</> : <><Database size={16} className="mr-2" /> Importar OCDS</>}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-none shadow-none border border-border/40" data-testid="configs-table">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-mono text-xs uppercase tracking-wider">Nombre</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">URL Base</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">Intervalo</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider">Estado</TableHead>
                <TableHead className="font-mono text-xs uppercase tracking-wider text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No hay configuraciones</TableCell></TableRow>
              ) : configs.map((cfg) => (
                <TableRow key={cfg.id} className="hover:bg-secondary/30 transition-colors duration-150" data-testid={`config-row-${cfg.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <GlobeSimple size={16} weight="duotone" className="text-[#002FA7]" />
                      <span className="font-medium text-sm">{cfg.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground max-w-[250px] truncate">{cfg.base_url}</TableCell>
                  <TableCell className="font-mono text-xs">{cfg.schedule_interval} min</TableCell>
                  <TableCell>
                    <Badge className={`rounded-none text-[10px] font-mono ${cfg.is_active ? "bg-[#2A9D8F] text-white" : "bg-secondary text-muted-foreground"}`}>
                      {cfg.is_active ? "Activo" : "Inactivo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" onClick={() => handleStartJob(cfg.id)} data-testid={`start-job-${cfg.id}`}>
                        <Play size={14} weight="fill" className="text-[#2A9D8F]" />
                      </Button>
                      <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" onClick={() => openEdit(cfg)} data-testid={`edit-config-${cfg.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" className="rounded-none h-8 w-8" onClick={() => handleDelete(cfg.id)} data-testid={`delete-config-${cfg.id}`}>
                        <Trash size={14} className="text-[#E63946]" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Config Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-lg" data-testid="config-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editing ? "Editar Configuración" : "Nueva Fuente de Datos"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Nombre</Label>
              <Input data-testid="config-name" className="rounded-none h-10 bg-card" placeholder="GuateCompras - Portal" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">URL Base</Label>
              <Input data-testid="config-url" className="rounded-none h-10 bg-card font-mono text-sm" placeholder="https://www.guatecompras.gt" value={form.base_url} onChange={e => setForm({ ...form, base_url: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase text-muted-foreground">Rutas de búsqueda (separadas por coma)</Label>
              <Input data-testid="config-paths" className="rounded-none h-10 bg-card font-mono text-sm" placeholder="/search, /concursos" value={form.search_paths} onChange={e => setForm({ ...form, search_paths: e.target.value })} />
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase text-muted-foreground">Selector Título</Label>
                <Input className="rounded-none h-9 bg-card font-mono text-xs" placeholder="h3, .title" value={form.selectors_title} onChange={e => setForm({ ...form, selectors_title: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase text-muted-foreground">Selector Entidad</Label>
                <Input className="rounded-none h-9 bg-card font-mono text-xs" placeholder=".entity, .org" value={form.selectors_entity} onChange={e => setForm({ ...form, selectors_entity: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div className="space-y-1.5">
                <Label className="text-xs font-mono uppercase text-muted-foreground">Intervalo (min)</Label>
                <Input data-testid="config-interval" type="number" className="rounded-none h-9 bg-card" value={form.schedule_interval} onChange={e => setForm({ ...form, schedule_interval: e.target.value })} />
              </div>
              <div className="flex items-center gap-3 pb-1">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="config-active-toggle" />
                <Label className="text-sm">{form.is_active ? "Activo" : "Inactivo"}</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button data-testid="save-config-button" className="rounded-none bg-[#002FA7] hover:bg-[#001D66] text-white" onClick={handleSave} disabled={saving || !form.name || !form.base_url}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
